import CoreExtension from '@/core/CoreExtension'
import { mapActions } from "vuex"

// consult-view tag -> PMH plugin key. Tags with no PMH widget (letters,
// life-style, ontslagbrief, cbb-formulier) are intentionally omitted.
const CONSULT_VIEW_TAG_TO_KEY = {
    'decursus': 'decursus',
    'chronic-prescriptions': 'chronic-prescriptions',
    'vital-functions': 'vital-functions',
    'measurements': 'measurements',
    'prescriptions': 'prescriptions',
    'blood-type': 'blood-type',
    'attests': 'attests',
    'medical-cards': 'medicalcards',
    'history': 'history',
    'allergies': 'allergies',
    'diagnostic-referrals': 'diagnostic-referrals',
    'vaccinations': 'vaccinations',
    'maternity-consult': 'maternity',
}

const PLUGIN_DEFINITIONS = {
    'history': { permission_key: 'history', key: 'history', icon: 'fa-files-medical', title: 'General History'},
    'diagnostic-categories': { permission_key: 'patient', key: 'diagnostic-categories', icon: 'fa-comment-medical', title: 'Diagnostic Categories'},
    'blood-type': { permission_key: 'bloodtype', key: 'blood-type', icon: 'fa-droplet', title: 'Blood Type'},
    'disabilities': { permission_key: 'disabilities', key: 'disabilities', icon: 'fa-circle-exclamation', title: 'Disabilities'},
    'chronic-prescriptions': { permission_key: 'prescription', key: 'chronic-prescriptions', icon: 'fa-prescription-bottle-medical', title: 'Chronic Prescriptions'},
    'episodes': { permission_key: 'episodes', key: 'episodes', icon: 'fa-heart-pulse', title: 'Episodes'},
    'decursus': { permission_key: 'decursus', key: 'decursus', icon: 'fa-notes-medical', title: 'Decursus'},
    'referrals': { permission_key: 'referral', key: 'referrals', icon: 'fa-forward', title: 'Referrals'},
    'allergies': { permission_key: 'allergies', key: 'allergies', icon: 'fa-hand-dots', title: 'Allergies'},
    'prescriptions': { permission_key: 'prescription', key: 'prescriptions', icon: 'fa-prescription-bottle-pill', title: 'Prescriptions'},
    'measurements': { permission_key: 'measurements', key: 'measurements', icon: 'fa-weight-scale', title: 'Measurements'},
    'vital-functions': { permission_key: 'vitals', key: 'vital-functions', icon: 'fa-stethoscope', title: 'Vital Functions'},
    'medicalcards': { permission_key: 'medicalcard', key: 'medicalcards', icon: 'fa-folder-medical', title: 'Medical Cards'},
    'diagnostic-referrals': { permission_key: 'lab', key: 'diagnostic-referrals', icon: 'fa-flask-round-potion', title: 'Diagnostic Referrals'},
    'vaccinations': { permission_key: 'vaccination', key: 'vaccinations', icon: 'fa-syringe', title: 'Vaccinations'},
    'procedures': { permission_key: 'procedure', key: 'procedures', icon: 'fa-scalpel', title: 'Procedures'},
    'attests': { permission_key: 'attest', key: 'attests', icon: 'fa-calendar-range', title: 'Attests'},
    'maternity': { permission_key: 'maternity', key: 'maternity', icon: 'fa-person-pregnant', title: 'Maternity'},
    'pregnancy-history': { permission_key: 'maternity_pregnancy', key: 'pregnancy-history', icon: 'fa-baby', title: 'Pregnancy History'},
}

// Fallback order for PMH widgets that have no consult-view equivalent, and
// for the very first render before the consult-view order has loaded.
const FALLBACK_ORDER = [
    'history', 'diagnostic-categories', 'blood-type', 'disabilities', 'chronic-prescriptions',
    'episodes', 'decursus', 'referrals', 'allergies', 'prescriptions', 'measurements',
    'vital-functions', 'medicalcards', 'diagnostic-referrals', 'vaccinations', 'procedures',
    'attests', 'maternity', 'pregnancy-history',
]

export default CoreExtension.extend({
	props: ['limit'],
	data() {
		return {
            useLimit: true,
            consultViewOrder: [],
		}
	},
    components: {
        "pmh-history": () => import("./History"),
        "pmh-diagnostic-categories": () => import("./DiagnosticCategories"),
        "pmh-blood-type": () => import("./BloodType"),
        "pmh-disabilities": () => import("./Disabilities"),
        "pmh-chronic-prescriptions": () => import("./ChronicPrescriptions"),
        "pmh-episodes": () => import("./Episodes"),
        "pmh-decursus": () => import("./Decursus"),
        "pmh-referrals": () => import("./Referrals"),
        "pmh-allergies": () => import("./Allergies"),
        "pmh-prescriptions": () => import("./Prescriptions"),
        "pmh-vital-functions": () => import("./VitalFunctions"),
        "pmh-measurements": () => import("./Measurements"),
        "pmh-medicalcards": () => import("./MedicalCards"),
        "pmh-diagnostic-referrals": () => import("./DiagnosticReferrals"),
        "pmh-vaccinations": () => import("./Vaccinations"),
        "pmh-procedures": () => import("./Procedures"),
        "pmh-attests": () => import("./Attests"),
        "pmh-maternity": () => import("./Maternity"),
        "pmh-pregnancy-history": () => import("./PregnancyHistory"),
    },
    computed: {
        plugins() {
            // Keys driven by the consult-view's current component order (the
            // logged-in physician's own view-builder layout, falling back to
            // the view's default order when they haven't customized it).
            const fromConsultView = this.consultViewOrder
                .map(tag => CONSULT_VIEW_TAG_TO_KEY[tag])
                .filter(key => key && PLUGIN_DEFINITIONS[key])

            // Everything else (no consult-view equivalent, or still loading)
            // keeps its fallback position, appended after the consult-view ones.
            const seen = new Set(fromConsultView)
            const remaining = FALLBACK_ORDER.filter(key => !seen.has(key))

            return [...fromConsultView, ...remaining].map(key => PLUGIN_DEFINITIONS[key])
        }
    },
    methods: {
        ...mapActions('viewcreator', ['getViewComponentOrder']),
        async loadConsultViewOrder() {
            this.consultViewOrder = await this.getViewComponentOrder('consult-view')
        }
    }
})
